import { GLOBAL_SPACING } from "./constants";
import { truncateLabel } from "./helpers";

function collectBodySubItems({ body, context, meshes, sites }) {
  const { hiddenTypes, mountedCamerasByBody } = context;
  const subItems = [];

  if (!hiddenTypes.has("mesh") && Array.isArray(body.geoms)) {
    body.geoms
      .filter((geom) => geom?.visualOnly === true)
      .forEach((geom) => {
        const meshDef = geom.mesh && meshes ? meshes[geom.mesh] : null;
        subItems.push({
          subType: "mesh",
          name: meshDef?.name || geom.name || geom.kind || geom.mesh || "visual",
          meshId: geom.mesh || null,
          uuid: geom.uuid ? String(geom.uuid) : null,
          parentBodyUuid: body.uuid,
        });
      });
  }

  if (!hiddenTypes.has("geom") && Array.isArray(body.geoms)) {
    body.geoms
      .filter((geom) => geom && geom.visualOnly !== true)
      .forEach((geom) => {
        subItems.push({
          subType: "geom",
          name: geom.name || geom.mesh || "collision",
          uuid: geom.uuid ? String(geom.uuid) : null,
          parentBodyUuid: body.uuid,
        });
      });
  }

  if (!hiddenTypes.has("site") && Array.isArray(body.sites)) {
    body.sites.forEach((siteUuid) => {
      const site = sites?.[siteUuid];
      if (!site) return;
      subItems.push({
        subType: site.name === "attachment_site" ? "attachment_site" : "site",
        name: site.name || "site",
        uuid: siteUuid,
        parentBodyUuid: body.uuid,
      });
    });
  }

  if (!hiddenTypes.has("inertial") && body.inertia) {
    subItems.push({
      subType: "inertial",
      name: body.inertia.name || "inertial",
      uuid: body.inertia.uuid ? String(body.inertia.uuid) : null,
      parentBodyUuid: body.uuid,
    });
  }

  if (!hiddenTypes.has("camera")) {
    (mountedCamerasByBody.get(body.uuid) ?? []).forEach((camera) => {
      subItems.push({
        subType: "camera",
        name: camera.name || "camera",
        uuid: camera.uuid,
        parentBodyUuid: body.uuid,
      });
    });
  }

  return subItems;
}

export function createBodyProcessor({ nodes, edges, idState, context, meshes, sites }) {
  const {
    satelliteYGap,
    baseLevelGap,
    minLevelSpacing,
    childSpacing,
    uniformSatXOffset,
    satHalfH,
    bodyJointActHalfH,
    contourSibSep,
  } = context;

  const processBody = (body, parentBodyNodeId, xOffset, y, crossbarY = null) => {
    const bodyNode = {
      id: idState.nextId++,
      name: body.name,
      uuid: body.uuid,
      type: "body",
      x: xOffset,
      y,
    };
    nodes.push(bodyNode);

    if (parentBodyNodeId !== null) {
      edges.push({
        id: `e-body-${parentBodyNodeId}-${bodyNode.id}`,
        from: parentBodyNodeId,
        to: bodyNode.id,
        edgeType: "body-parent-child",
        crossbarY,
      });
    }

    const subItems = collectBodySubItems({ body, context, meshes, sites });
    if (subItems.length > 0) {
      const totalH = (subItems.length - 1) * satelliteYGap;
      subItems.forEach((sub, index) => {
        const subNode = {
          id: idState.nextId++,
          name: sub.name,
          displayName: truncateLabel(sub.name),
          type: sub.subType,
          x: xOffset + uniformSatXOffset,
          y: y + index * satelliteYGap - totalH / 2,
          parentBodyId: bodyNode.id,
          uuid: sub.uuid,
          parentBodyUuid: sub.parentBodyUuid,
          meshId: sub.meshId,
        };
        nodes.push(subNode);
        edges.push({
          id: `e-sat-${bodyNode.id}-${subNode.id}`,
          from: bodyNode.id,
          to: subNode.id,
          edgeType: "body-satellite",
        });
      });
    }

    if (body.children?.length) {
      const maxChildHalf = Math.max(...body.children.map(satHalfH));
      const maxJointActHalf = Math.max(...body.children.map(bodyJointActHalfH));
      const jointClearance = 24;
      const childY =
        y +
        Math.max(
          minLevelSpacing,
          satHalfH(body) + baseLevelGap + maxChildHalf,
          2 * (satHalfH(body) + maxJointActHalf + jointClearance),
          2 * (maxChildHalf + maxJointActHalf + jointClearance)
        );

      if (body.children.length === 1) {
        processBody(body.children[0], bodyNode.id, xOffset, childY, null);
        return;
      }

      const childXOffsets = [0];
      for (let i = 1; i < body.children.length; i++) {
        childXOffsets.push(
          childXOffsets[i - 1] + contourSibSep(body.children[i - 1], body.children[i], childSpacing)
        );
      }
      const totalSpan = childXOffsets[childXOffsets.length - 1];
      const startCX = xOffset - totalSpan / 2;
      const satBottom = y + satHalfH(body);
      const myCrossbarY = Math.max(satBottom + 18, y + (childY - y) * 0.4);

      body.children.forEach((child, index) => {
        processBody(child, bodyNode.id, startCX + childXOffsets[index], childY, myCrossbarY);
      });
    }
  };

  return processBody;
}

export function layoutBodyRoots({ bodyTree, processBody, context }) {
  const {
    rootSpacing,
    flatGridXGap,
    flatGridYGap,
    getSubtreeWidth,
    getFlatRootHeight,
    computeRightContour,
    contourSibSep,
  } = context;
  let rightmostRootEdge = 0;
  const useFlatRootGrid = bodyTree.every(
    (body) => !Array.isArray(body.children) || body.children.length === 0
  );

  if (useFlatRootGrid) {
    const columnCount = Math.max(1, Math.min(3, bodyTree.length));
    const rowCount = Math.max(1, Math.ceil(bodyTree.length / columnCount));
    const rootItems = bodyTree.map((body, index) => ({
      body,
      column: index % columnCount,
      row: Math.floor(index / columnCount),
      width: getSubtreeWidth(body),
      height: getFlatRootHeight(body),
    }));

    const columnWidths = Array.from({ length: columnCount }, () => 0);
    const rowHeights = Array.from({ length: rowCount }, () => 0);
    rootItems.forEach((item) => {
      columnWidths[item.column] = Math.max(columnWidths[item.column], item.width);
      rowHeights[item.row] = Math.max(rowHeights[item.row], item.height);
    });

    const totalGridWidth =
      columnWidths.reduce((sum, itemWidth) => sum + itemWidth, 0) +
      flatGridXGap * Math.max(0, columnCount - 1);
    const totalGridHeight =
      rowHeights.reduce((sum, itemHeight) => sum + itemHeight, 0) +
      flatGridYGap * Math.max(0, rowCount - 1);

    const columnCenters = [];
    let cursorX = -totalGridWidth / 2;
    columnWidths.forEach((itemWidth) => {
      columnCenters.push(cursorX + itemWidth / 2);
      cursorX += itemWidth + flatGridXGap;
    });

    const rowCenters = [];
    let cursorY = -totalGridHeight / 2;
    rowHeights.forEach((itemHeight) => {
      rowCenters.push(cursorY + itemHeight / 2);
      cursorY += itemHeight + flatGridYGap;
    });

    rootItems.forEach((item) => {
      const rx = columnCenters[item.column];
      const ry = rowCenters[item.row];
      processBody(item.body, null, rx, ry);
      const contour = computeRightContour(item.body);
      rightmostRootEdge = Math.max(rightmostRootEdge, rx + (contour[0] ?? 100));
    });
    return rightmostRootEdge;
  }

  const rootXOffsets = [0];
  for (let i = 1; i < bodyTree.length; i++) {
    rootXOffsets.push(
      rootXOffsets[i - 1] + contourSibSep(bodyTree[i - 1], bodyTree[i], rootSpacing)
    );
  }
  const totalRootSpan = rootXOffsets[rootXOffsets.length - 1];
  const rootStartX = -totalRootSpan / 2;
  bodyTree.forEach((body, index) => {
    const rx = rootStartX + rootXOffsets[index];
    processBody(body, null, rx, 50);
    const contour = computeRightContour(body);
    rightmostRootEdge = Math.max(rightmostRootEdge, rx + (contour[0] ?? 100));
  });
  return rightmostRootEdge;
}

export function addWorldSites({ nodes, idState, bodies, sites, context, rightmostRootEdge }) {
  const { hiddenTypes, satelliteYGap } = context;
  if (hiddenTypes.has("site")) return rightmostRootEdge;

  const bodySiteUuids = new Set();
  Object.values(bodies || {}).forEach((body) => {
    if (!body || !Array.isArray(body.sites)) return;
    body.sites.forEach((ref) => {
      const siteUuid = typeof ref === "string" ? ref : ref?.uuid;
      if (siteUuid) bodySiteUuids.add(String(siteUuid));
    });
  });

  const worldSites = Object.values(sites || {}).filter((site) => {
    if (!site?.uuid) return false;
    return !bodySiteUuids.has(String(site.uuid));
  });

  if (worldSites.length === 0) return rightmostRootEdge;

  const siteX = nodes.length > 0 ? rightmostRootEdge + GLOBAL_SPACING : 0;
  const totalSiteH = (worldSites.length - 1) * satelliteYGap;
  worldSites.forEach((site, index) => {
    nodes.push({
      id: idState.nextId++,
      name: site.name || "site",
      displayName: truncateLabel(site.name || "site"),
      uuid: site.uuid,
      type: site.name === "attachment_site" ? "attachment_site" : "site",
      x: siteX,
      y: 50 + index * satelliteYGap - totalSiteH / 2,
      parentBodyUuid: null,
      data: site,
    });
  });
  return Math.max(rightmostRootEdge, siteX + 120);
}

export function addWorldCameras({ nodes, idState, context, rightmostRootEdge }) {
  const { hiddenTypes, worldCameras, satelliteYGap } = context;
  if (hiddenTypes.has("camera") || worldCameras.length === 0) return;

  const camX = nodes.length > 0 ? rightmostRootEdge + GLOBAL_SPACING : 0;
  const totalCamH = (worldCameras.length - 1) * satelliteYGap;
  worldCameras.forEach((camera, index) => {
    nodes.push({
      id: idState.nextId++,
      name: camera.name || "camera",
      displayName: truncateLabel(camera.name || "camera"),
      uuid: camera.uuid,
      type: "camera",
      x: camX,
      y: 50 + index * satelliteYGap - totalCamH / 2,
      data: camera,
    });
  });
}
